import { SignInOptions } from 'next-auth/react';
import { TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import * as ApplicationConfigurationService from 'src/services/config/ApplicationConfigurationService.client';
import { zillowSignIn } from './zillowSignIn/zillowSignIn';

declare global {
    interface Window {
        SESSION_START_REQUESTED: boolean;
    }
}

/**
 * Since multiple components and business logic paths can cause a sign in attempt,
 * it can be difficult to debug why session logins were attempted. To address this, this function
 * requires that a justification for performing a login is provided, which is then logged to the console in lower
 * environments for debugging.
 *
 * Additionally, this function abstracts the logic around ensuring we leverage the required "signin bounce" experience
 * that is aimed at resolving the back-button-breaking-on-authorization-code-flow-pages problem.
 *
 * The function determines the callback URL by:
 * 1. Checking for existing callbackUrl query parameters to prevent infinite loops
 * 2. Validating callback URLs against an allowlist for security
 * 3. Falling back to the current pathname if no valid callback is found
 *
 * @param justification - why the session should be started
 */
export const handleSessionStart = (
    justification: string,
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>,
    signInOptions: SignInOptions = {},
    authorizationParams: Record<string, string> = {}
) => {
    if (window.SESSION_START_REQUESTED) {
        return;
    }
    window.SESSION_START_REQUESTED = true;
    const configService = ApplicationConfigurationService.ConfigProvider.getInstance();
    if (configService.runtimeConfig.isLowerEnvironment) {
        // eslint-disable-next-line no-console
        console.info('Application Session Started - ', justification);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const existingCallbackUrl = urlParams.get('callbackUrl');

    /**
     * Determines the appropriate callback URL for authentication, preferring existing query parameters
     * to prevent infinite loops when called from the signin bounce page.
     *
     */
    const callbackUrl = configService.routeConfiguration.getSafeCallbackUrl(
        existingCallbackUrl,
        window.location.pathname
    );
    const updatedSignInOptions: SignInOptions = {
        ...signInOptions,
        callbackUrl,
    };

    zillowSignIn(experiments, updatedSignInOptions, authorizationParams);
};
